import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, LayoutDashboard } from "lucide-react";
import { api, logout, INTEREST_LABELS } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Portal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [consults, setConsults] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
      } catch {
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [location.state, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/leads/mine").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/consultations/mine").then((r) => setConsults(r.data)).catch(() => {});
  }, [user]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center" data-testid="portal-loading">
        <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#8A8D98] animate-pulse">Loading your portal…</p>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B0C10] px-6 md:px-12 py-10" data-testid="client-portal">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between pb-8 border-b border-white/10">
          <Logo testId="portal-home-link" />
          <div className="flex items-center gap-4">
            {user.is_admin && (
              <Link to="/land-acquisition" data-testid="portal-admin-link" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#C8A97E] hover:text-[#F8F9FA] transition-colors">
                <LayoutDashboard size={14} /> Land Deal Pipeline
              </Link>
            )}
            <button onClick={handleLogout} data-testid="portal-logout-button" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-red-400 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="py-12 flex items-center gap-6">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border border-[#C8A97E]/40" data-testid="portal-avatar" />
          ) : (
            <div className="w-16 h-16 rounded-full border border-[#C8A97E]/40 flex items-center justify-center font-display text-2xl text-[#C8A97E]">
              {user.name?.[0]}
            </div>
          )}
          <div>
            <p className="font-mono-lux text-[10px] uppercase tracking-[0.3em] text-[#C8A97E]">Verified Google Client</p>
            <h1 className="font-display text-3xl md:text-4xl text-[#F8F9FA] mt-1" data-testid="portal-user-name">{user.name}</h1>
            <p className="text-sm text-[#8A8D98]">{user.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-16">
          <section className="border border-white/10 bg-white/[0.02] p-8" data-testid="portal-enquiries-panel">
            <h2 className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#C8A97E] mb-6">My Enquiries</h2>
            {leads.length === 0 ? (
              <p className="text-sm text-[#8A8D98]">No enquiries yet. <Link to="/#contact" className="text-[#C8A97E] underline underline-offset-4" data-testid="portal-new-enquiry-link">Submit your first interest</Link>.</p>
            ) : (
              <ul className="space-y-4">
                {leads.map((l) => (
                  <li key={l.lead_id} className="flex items-center justify-between border-b border-white/5 pb-4" data-testid={`portal-lead-${l.lead_id}`}>
                    <div>
                      <p className="text-sm text-[#F8F9FA]">{INTEREST_LABELS[l.interest]}</p>
                      <p className="font-mono-lux text-[10px] text-[#8A8D98] mt-1">{new Date(l.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${l.status === "qualified" ? "border-[#C8A97E]/50 text-[#C8A97E]" : "border-white/15 text-[#8A8D98]"}`}>
                      {l.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border border-white/10 bg-white/[0.02] p-8" data-testid="portal-consultations-panel">
            <h2 className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#C8A97E] mb-6">My Consultations</h2>
            {consults.length === 0 ? (
              <p className="text-sm text-[#8A8D98]">No consultations booked. <Link to="/#contact" className="text-[#C8A97E] underline underline-offset-4" data-testid="portal-book-link">Book a slot</Link>.</p>
            ) : (
              <ul className="space-y-4">
                {consults.map((c) => (
                  <li key={c.consultation_id} className="flex items-center justify-between border-b border-white/5 pb-4" data-testid={`portal-consult-${c.consultation_id}`}>
                    <div>
                      <p className="text-sm text-[#F8F9FA]">{c.topic || "General advisory"}</p>
                      <p className="font-mono-lux text-[10px] text-[#8A8D98] mt-1">{c.date} · {c.time_slot}</p>
                    </div>
                    <span className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-white/15 text-[#8A8D98]">{c.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
